/**
 * The 8 brand presets the user can pick from. Mirrors the Flutter app
 * (`lib/core/theme/app_color_scheme.dart`) so a tenant on phone + web
 * sees the same palette.
 *
 * Each preset is a 10-stop ramp generated from a single seed hue. All
 * other UI tokens (surfaces, ink, success/warn/danger) are independent
 * of the brand and live in `themeRuntime.ts`.
 */

export type ThemePresetId =
  | 'emerald'
  | 'indigo'
  | 'rose'
  | 'amber'
  | 'slate'
  | 'sky'
  | 'violet'
  | 'crimson';

export interface ThemePreset {
  id: ThemePresetId;
  label: string;
  /** 10-stop ramp keyed 50..900 — RGB triplets used by Tailwind alpha syntax. */
  ramp: Record<50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900, string>;
}

/** Each ramp is `R G B` (no commas) so Tailwind can do `rgb(var(--brand-500) / 0.5)`. */
export const PRESETS: ThemePreset[] = [
  {
    id: 'emerald',
    label: 'Emerald',
    ramp: {
      50: '236 253 245', 100: '209 250 229', 200: '167 243 208',
      300: '110 231 183', 400: '52 211 153',  500: '16 185 129',
      600: '5 150 105',   700: '4 120 87',     800: '6 95 70',
      900: '6 78 59',
    },
  },
  {
    id: 'indigo',
    label: 'Indigo',
    ramp: {
      50: '238 242 255', 100: '224 231 255', 200: '199 210 254',
      300: '165 180 252', 400: '129 140 248', 500: '99 102 241',
      600: '79 70 229',   700: '67 56 202',    800: '55 48 163',
      900: '49 46 129',
    },
  },
  {
    id: 'rose',
    label: 'Rose',
    ramp: {
      50: '255 241 242', 100: '255 228 230', 200: '254 205 211',
      300: '253 164 175', 400: '251 113 133', 500: '244 63 94',
      600: '225 29 72',   700: '190 18 60',    800: '159 18 57',
      900: '136 19 55',
    },
  },
  {
    id: 'amber',
    label: 'Amber',
    ramp: {
      50: '255 251 235', 100: '254 243 199', 200: '253 230 138',
      300: '252 211 77',  400: '251 191 36',  500: '245 158 11',
      600: '217 119 6',   700: '180 83 9',     800: '146 64 14',
      900: '120 53 15',
    },
  },
  {
    id: 'slate',
    label: 'Slate',
    ramp: {
      50: '248 250 252', 100: '241 245 249', 200: '226 232 240',
      300: '203 213 225', 400: '148 163 184', 500: '100 116 139',
      600: '71 85 105',   700: '51 65 85',     800: '30 41 59',
      900: '15 23 42',
    },
  },
  {
    id: 'sky',
    label: 'Sky',
    ramp: {
      50: '240 249 255', 100: '224 242 254', 200: '186 230 253',
      300: '125 211 252', 400: '56 189 248',  500: '14 165 233',
      600: '2 132 199',   700: '3 105 161',    800: '7 89 133',
      900: '12 74 110',
    },
  },
  {
    id: 'violet',
    label: 'Violet',
    ramp: {
      50: '245 243 255', 100: '237 233 254', 200: '221 214 254',
      300: '196 181 253', 400: '167 139 250', 500: '139 92 246',
      600: '124 58 237',  700: '109 40 217',   800: '91 33 182',
      900: '76 29 149',
    },
  },
  {
    id: 'crimson',
    label: 'Crimson',
    ramp: {
      50: '254 242 242', 100: '254 226 226', 200: '254 202 202',
      300: '252 165 165', 400: '248 113 113', 500: '239 68 68',
      600: '220 38 38',   700: '185 28 28',    800: '153 27 27',
      900: '127 29 29',
    },
  },
];

export const DEFAULT_PRESET: ThemePresetId =
  (import.meta.env.VITE_DEFAULT_THEME as ThemePresetId) || 'emerald';
